import React from 'react';
import { Box, Typography, Grid, MenuItem, useTheme, alpha, Container, Card } from '@mui/material';
import { StyledTextField, StyledSelect } from './index';
import { usePermissions } from '../../../utils/permissions';
import { Home as HomeIcon } from '@mui/icons-material';

const countries = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' }
];

const statesByCountry = {
  'US': [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ],
  'CA': [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador', 
    'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island', 
    'Quebec', 'Saskatchewan', 'Yukon'
  ],
  'GB': [
    'England', 'Scotland', 'Wales', 'Northern Ireland'
  ],
  'AU': [
    'Australian Capital Territory', 'New South Wales', 'Northern Territory', 'Queensland', 
    'South Australia', 'Tasmania', 'Victoria', 'Western Australia'
  ]
};

const BusinessAddress = ({ 
  companyInfo, 
  handleInputChange, 
  loading, 
  requiredFields, 
  validationErrors 
}) => {
  const theme = useTheme();
  const { hasPermission, canAccessPage, canEditPage } = usePermissions();
  
  const isFieldRequired = (fieldName) => {
    return requiredFields['business-address']?.includes(fieldName) || false;
  };

  const getFieldError = (fieldName) => {
    return validationErrors[fieldName] || '';
  };

  // Permission check for business address access
  if (!canAccessPage('business_address')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '50vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 6,
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 500,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, transparent)`,
              zIndex: 0,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <HomeIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Business Address Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              fontWeight: 500
            }}>
              You don't currently have the necessary permissions to access the Business Address settings. Contact your system administrator to request access or verify your role permissions.
            </Typography>
          </Card>
        </Box>
      </Container>
    );
  }

  const canEditSettings = canEditPage('business_address');

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        mb: 4,
        p: 4,
        borderRadius: 4,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: 'white',
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '200px',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
          borderRadius: '50%',
          transform: 'translate(50%, -50%)',
          zIndex: 0
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>
          <Typography variant="h3" sx={{ 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.2rem',
            mb: 1,
            lineHeight: 1.2
          }}>
            Business Address
          </Typography>
          
          <Typography variant="body1" sx={{ 
            color: 'text.secondary', 
            fontWeight: 500,
            fontSize: '1rem',
            maxWidth: '400px'
          }}>
            Set your official business location and mailing address information
          </Typography>
        </Box>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <StyledTextField 
            label="Street Address" 
            name="address"
            value={companyInfo.address} 
            onChange={handleInputChange} 
            required={isFieldRequired('address')}
            error={!!getFieldError('address')}
            helperText={getFieldError('address')}
            disabled={loading || !canEditSettings}
            placeholder="123 Business Street"
          />
        </Grid>
        <Grid item xs={12}>
          <StyledTextField 
            label="Address Line 2" 
            name="address_2"
            value={companyInfo.address_2} 
            onChange={handleInputChange} 
            disabled={loading || !canEditSettings}
            placeholder="Suite, Unit, etc."
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledTextField 
            label="City" 
            name="city"
            value={companyInfo.city} 
            onChange={handleInputChange} 
            required={isFieldRequired('city')}
            error={!!getFieldError('city')}
            helperText={getFieldError('city')}
            disabled={loading || !canEditSettings}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledTextField 
            label="ZIP Code" 
            name="zip_code"
            value={companyInfo.zip_code} 
            onChange={handleInputChange} 
            required={isFieldRequired('zip_code')}
            error={!!getFieldError('zip_code')}
            helperText={getFieldError('zip_code')}
            disabled={loading || !canEditSettings}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledSelect
            label="Country"
            name="country"
            value={companyInfo.country}
            onChange={handleInputChange}
            required={isFieldRequired('country')}
            error={!!getFieldError('country')}
            helperText={getFieldError('country')}
            disabled={loading || !canEditSettings}
          >
            {countries.map((country) => (
              <MenuItem key={country.code} value={country.code}>{country.name}</MenuItem>
            ))}
          </StyledSelect>
        </Grid>
        {statesByCountry[companyInfo.country] && (
          <Grid item xs={12} md={6}>
            <StyledSelect
              label="State/Province"
              name="state"
              value={companyInfo.state}
              onChange={handleInputChange}
              required={isFieldRequired('state')}
              error={!!getFieldError('state')}
              helperText={getFieldError('state')}
              disabled={loading || !canEditSettings}
            >
              {statesByCountry[companyInfo.country].map((state) => (
                <MenuItem key={state} value={state}>{state}</MenuItem>
              ))}
            </StyledSelect>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default BusinessAddress;